import {
  stepTripCancellationTugoLookup,
} from './claimCodeMapper';

function getActiveLookup() {
  return stepTripCancellationTugoLookup;
}

const HEALTH_SUB_QUESTION_PREFIXES = [
  'youOrTravellingCompanion',
  'familyMember',
  'businessPartnerOrEmployer',
  'hostAtDestination',
];

const PREGNANCY_ADOPTION_SUB_QUESTION_PREFIXES = [
  'legalAdoption',
  'pregnancyDiagnosed',
  'complicationsOfPregnancy',
];

const EMPLOYMENT_EDUCATION_SUB_QUESTION_PREFIXES = [
  'jobTransfer',
  'involuntaryLossOfEmployment',
  'cancellationOfBusinessMeeting',
  'cancellationOfConference',
];

const LEGAL_ISSUES_SUB_QUESTION_PREFIXES = [
  'canadianGovernmentAdvisory',
  'juryDutyCourtProceeding',
  'summonedToService',
  'officialTravelAdvisory',
  'nonIssuanceOfVisa',
];

const CARRIER_ACCIDENT_WEATHER_SUB_QUESTION_PREFIXES = [
  'missedConnection',
  'fourHoursNoConnection',
  'cancellationOfCommonCarrier',
  'tourOperatorCancellation',
  'accidentOnWay',
  'delayOfPrivateVehicle',
];

const OTHER_REASONS_SUB_QUESTION_PREFIXES = [
  'naturalDisaster',
  'unforeseeableEvent',
];

// Maps expense category keys to their lookup keys in the claim code mapper
const EXPENSE_CATEGORY_CONFIG = [
  {
    categoryKey: 'nonRefundablePrePaidTravelCosts',
    itemsField: 'NonRefundablePrePaidTravelCostsItems',
    lookupKey: 'nonRefundablePrePaidTravelCosts',
    description: 'Non-Refundable Pre-paid travel costs',
  },
  {
    categoryKey: 'nonRefundablePrePaidTransportationCosts',
    itemsField: 'NonRefundablePrePaidTransportationCostsItems',
    lookupKey: 'nonRefundablePrePaidTransportationCosts',
    description: 'Non-Refundable Pre-paid transportation costs',
  },
  {
    categoryKey: 'commercialAccommodationToResumeTravel',
    itemsField: 'CommercialAccommodationToResumeTravelItems',
    lookupKey: 'commercialAccommodationToResumeTravel',
    description: 'Commercial Accommodation to resume travel',
  },
  {
    categoryKey: 'changeFeesIfCancellingPortionOfTrip',
    itemsField: 'ChangeFeesIfCancellingPortionOfTripItems',
    lookupKey: 'changeFeesIfCancellingPortionOfTrip',
    description: 'Change Fees if cancelling portion of trip',
  },
  {
    categoryKey: 'singleSupplement',
    itemsField: 'SingleSupplementItems',
    lookupKey: 'singleSupplement',
    description: 'Single Supplement',
  },
  {
    categoryKey: 'nonRefundablePrePaidTourCosts',
    itemsField: 'NonRefundablePrePaidTourCostsItems',
    lookupKey: 'nonRefundablePrePaidTourCosts',
    description: 'Non-Refundable Pre-paid tour costs',
  },
  {
    categoryKey: 'nonRefundablePrePaidTransportationCostsNotPartOfTour',
    itemsField: 'NonRefundablePrePaidTransportationCostsNotPartOfTourItems',
    lookupKey: 'nonRefundablePrePaidTransportationCostsNotPartOfTour',
    description: 'Non-Refundable Pre-paid transportation costs that is not part of the tour',
  },
];

function toJSArray(immutableOrArray) {
  if (!immutableOrArray) return [];
  if (typeof immutableOrArray.toJS === 'function') return immutableOrArray.toJS();
  return immutableOrArray;
}

function getClaimCodeFromLookup(lookupKey) {
  const expenseLookup = getActiveLookup();
  const entry = expenseLookup[lookupKey];
  if (!entry) {
    return null;
  }
  if (entry.claimCode) {
    return entry.claimCode;
  }
  if (entry.associatedClaimCodes && entry.associatedClaimCodes.length > 0) {
    return entry.associatedClaimCodes[0];
  }
  return null;
}

function mapExpenseItem(item, claimCode) {
  const selectedClaimForArray = (!Array.isArray(item.selectedClaimFor))
    ? (item.selectedClaimFor && typeof item.selectedClaimFor.toJS === 'function'
      ? item.selectedClaimFor.toJS()
      : item.selectedClaimFor || [])
    : item.selectedClaimFor;
  const claimForInsuredIds = selectedClaimForArray.map((claimFor) => claimFor.policyRoleID);

  if (!item || !item.amountClaimed || !item.currency) {
    return null;
  }

  return {
    shortDescription: `${item.descriptionOfItem}`,
    dateOfExpense: item.dateOfExpenses,
    clientCost: item.amountClaimed,
    currency: item.currency,
    rate: null,
    requestedAmount: null,
    claimTypeCode: claimCode,
    claimForInsuredIds,
    claimForInsureds: selectedClaimForArray,
  };
}

function mapToParentSection() {
  return {};
}

function mapToQuestionnaire() {
  return [];
}

function collectExpensesForPrefixes(data, prefixes, activeSubQuestions) {
  const expenses = [];

  prefixes.forEach((prefix) => {
    if (!activeSubQuestions.includes(prefix)) return;

    const selectedExpenses = toJSArray(data[`${prefix}SelectedExpenses`]);
    if (!selectedExpenses || selectedExpenses.length === 0) return;

    EXPENSE_CATEGORY_CONFIG.forEach((config) => {
      if (!selectedExpenses.includes(config.categoryKey)) return;

      // For change fees, only include if a radio option is selected
      if (config.categoryKey === 'changeFeesIfCancellingPortionOfTrip') {
        const changeFeeOption = data[`${prefix}ChangeFeeOption`] || '';
        if (!changeFeeOption) return;
      }

      const claimCode = getClaimCodeFromLookup(config.lookupKey);
      if (!claimCode) return;

      const items = toJSArray(data[`${prefix}${config.itemsField}`]);
      if (!items || items.length === 0) return;

      items.forEach((item) => {
        const mapped = mapExpenseItem(item, claimCode);
        if (mapped) {
          expenses.push(mapped);
        }
      });
    });
  });

  return expenses;
}

function mapToExpenses(data) {
  if (!data) {
    return [];
  }

  const allExpenses = [];

  const healthSubQuestions = toJSArray(data.healthSubQuestions);
  if (healthSubQuestions && healthSubQuestions.length > 0) {
    allExpenses.push(...collectExpensesForPrefixes(data, HEALTH_SUB_QUESTION_PREFIXES, healthSubQuestions));
  }

  const pregnancyAdoptionSubQuestions = toJSArray(data.pregnancyAdoptionSubQuestions);
  if (pregnancyAdoptionSubQuestions && pregnancyAdoptionSubQuestions.length > 0) {
    allExpenses.push(...collectExpensesForPrefixes(data, PREGNANCY_ADOPTION_SUB_QUESTION_PREFIXES, pregnancyAdoptionSubQuestions));
  }

  const employmentEducationSubQuestions = toJSArray(data.employmentEducationSubQuestions);
  if (employmentEducationSubQuestions && employmentEducationSubQuestions.length > 0) {
    allExpenses.push(...collectExpensesForPrefixes(data, EMPLOYMENT_EDUCATION_SUB_QUESTION_PREFIXES, employmentEducationSubQuestions));
  }

  const legalIssuesSubQuestions = toJSArray(data.legalIssuesSubQuestions);
  if (legalIssuesSubQuestions && legalIssuesSubQuestions.length > 0) {
    allExpenses.push(...collectExpensesForPrefixes(data, LEGAL_ISSUES_SUB_QUESTION_PREFIXES, legalIssuesSubQuestions));
  }

  const carrierAccidentWeatherSubQuestions = toJSArray(data.carrierAccidentWeatherSubQuestions);
  if (carrierAccidentWeatherSubQuestions && carrierAccidentWeatherSubQuestions.length > 0) {
    allExpenses.push(...collectExpensesForPrefixes(data, CARRIER_ACCIDENT_WEATHER_SUB_QUESTION_PREFIXES, carrierAccidentWeatherSubQuestions));
  }

  const otherReasonsSubQuestions = toJSArray(data.otherReasonsSubQuestions);
  if (otherReasonsSubQuestions && otherReasonsSubQuestions.length > 0) {
    allExpenses.push(...collectExpensesForPrefixes(data, OTHER_REASONS_SUB_QUESTION_PREFIXES, otherReasonsSubQuestions));
  }

  return allExpenses;
}

function mapToClaimExpensesLevel(data, claimTypes, selectedBenefits) {
  const selected = toJSArray(selectedBenefits);
  const expenseLookup = getActiveLookup();
  const claimCodeList = expenseLookup.claimCodeList || [];
  const list = [];

  if (
    selected.length > 0 &&
    !selected.some((sel) => claimCodeList.includes(sel.claimCode))
  ) {
    return list;
  }

  if (!claimTypes || claimTypes.length === 0 || claimCodeList.length === 0) {
    return list;
  }

  const allExpenses = mapToExpenses(data);

  // Determine which claim codes are "active" (category selected even if no items yet)
  const activeClaimCodes = new Set();

  function collectActiveClaimCodes(prefixes, activeSubQuestions) {
    prefixes.forEach((prefix) => {
      if (!activeSubQuestions.includes(prefix)) return;
      const selectedExpenses = toJSArray(data[`${prefix}SelectedExpenses`]);
      if (!selectedExpenses || selectedExpenses.length === 0) return;

      EXPENSE_CATEGORY_CONFIG.forEach((config) => {
        if (!selectedExpenses.includes(config.categoryKey)) return;
        if (config.categoryKey === 'changeFeesIfCancellingPortionOfTrip') {
          const changeFeeOption = data[`${prefix}ChangeFeeOption`] || '';
          if (!changeFeeOption) return;
        }
        const claimCode = getClaimCodeFromLookup(config.lookupKey);
        if (claimCode) activeClaimCodes.add(claimCode);
      });
    });
  }

  const healthSubQuestions = toJSArray(data ? data.healthSubQuestions : []);
  collectActiveClaimCodes(HEALTH_SUB_QUESTION_PREFIXES, healthSubQuestions);

  const pregnancyAdoptionSubQuestions = toJSArray(data ? data.pregnancyAdoptionSubQuestions : []);
  collectActiveClaimCodes(PREGNANCY_ADOPTION_SUB_QUESTION_PREFIXES, pregnancyAdoptionSubQuestions);

  const employmentEducationSubQuestions = toJSArray(data ? data.employmentEducationSubQuestions : []);
  collectActiveClaimCodes(EMPLOYMENT_EDUCATION_SUB_QUESTION_PREFIXES, employmentEducationSubQuestions);

  const legalIssuesSubQuestions = toJSArray(data ? data.legalIssuesSubQuestions : []);
  collectActiveClaimCodes(LEGAL_ISSUES_SUB_QUESTION_PREFIXES, legalIssuesSubQuestions);

  const carrierAccidentWeatherSubQuestions = toJSArray(data ? data.carrierAccidentWeatherSubQuestions : []);
  collectActiveClaimCodes(CARRIER_ACCIDENT_WEATHER_SUB_QUESTION_PREFIXES, carrierAccidentWeatherSubQuestions);

  const otherReasonsSubQuestions = toJSArray(data ? data.otherReasonsSubQuestions : []);
  collectActiveClaimCodes(OTHER_REASONS_SUB_QUESTION_PREFIXES, otherReasonsSubQuestions);

  // Map claim types to output - TC-1237: group under parent benefit for documents
  const parentClaimCode = getClaimCodeFromLookup('parentBenefit');
  const parentClaimType = parentClaimCode ? claimTypes.find((item) => item.claimCode === parentClaimCode) : null;

  if (parentClaimType && activeClaimCodes.size > 0) {
    list.push({
      claimCode: parentClaimCode,
      description: 'Trip Cancellation',
      claimTypeId: parentClaimType.claimTypeId,
      requiredDocuments: parentClaimType.requiredDocuments,
      documentClaimCodeLookupKey: 'parentBenefit',
      expenses: allExpenses,
    });
  } else {
    // Fallback: individual items if parent not available
    claimTypes.forEach((claimType) => {
      if (!activeClaimCodes.has(claimType.claimCode)) return;

      const config = EXPENSE_CATEGORY_CONFIG.find((c) => {
        const code = getClaimCodeFromLookup(c.lookupKey);
        return code === claimType.claimCode;
      });

      const filteredExpenses = allExpenses.filter((e) => e.claimTypeCode === claimType.claimCode);

      list.push({
        claimCode: claimType.claimCode,
        description: config ? config.description : 'Trip Cancellation',
        claimTypeId: claimType.claimTypeId,
        requiredDocuments: claimType.requiredDocuments,
        expenses: filteredExpenses,
      });
    });
  }

  return list;
}

export default {
  mapToParentSection,
  mapToQuestionnaire,
  mapToExpenses,
  mapToClaimExpensesLevel,
};



// WEBPACK FOOTER //
// ./app/containers/FastCover/dataMapper/stepTripCancellationTugoDataMapper.js